import api from "../../services/api";
import { GetBaseURL } from "../utils/urlUtils";
import type {
  AssistantAction,
  AssistantEntity,
  AssistantMode,
  DisplayItem,
  StreamEvent,
  ThreadSummary,
} from "./types";

const BASE = "/api/assistant";

export class AssistantHttpError extends Error {
  status: number;
  code?: string;
  body?: any;
  constructor(message: string, status: number, code?: string, body?: any) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** True when an axios error (or our own) is the plan gate. */
export function isUpgradeError(err: any): boolean {
  if (err instanceof AssistantHttpError) return err.code === "upgrade_required";
  return err?.response?.status === 403 && err?.response?.data?.error === "upgrade_required";
}

function pickArray<T>(data: any, ...keys: string[]): T[] {
  if (Array.isArray(data)) return data;
  for (const k of keys) {
    if (Array.isArray(data?.[k])) return data[k];
  }
  return [];
}

// ── Non-streaming calls (axios, interceptor handles auth) ──
// The panel shows its own Elite screen, so these skip the global upgrade modal.
const NO_GATE = { skipUpgradeEvent: true };

export const assistantApi = {
  listThreads: async (): Promise<ThreadSummary[]> => {
    const res = await api.get(`${BASE}/threads`, NO_GATE);
    return pickArray<ThreadSummary>(res.data, "data", "threads");
  },
  createThread: async (mode: AssistantMode): Promise<ThreadSummary> => {
    const res = await api.post(`${BASE}/threads`, { mode }, NO_GATE);
    return res.data?.thread || res.data?.data || res.data;
  },
  getThread: async (
    id: string
  ): Promise<{
    thread: ThreadSummary & { items: DisplayItem[] };
    pendingActions: AssistantAction[];
  }> => {
    const res = await api.get(`${BASE}/threads/${id}`, NO_GATE);
    const thread = res.data?.thread || res.data?.data || {};
    return {
      thread: { ...thread, items: Array.isArray(thread.items) ? thread.items : [] },
      pendingActions: pickArray<AssistantAction>(res.data, "pendingActions"),
    };
  },
  deleteThread: (id: string) => api.delete(`${BASE}/threads/${id}`, NO_GATE),
  setThreadMode: (id: string, mode: AssistantMode) =>
    api.patch(`${BASE}/threads/${id}`, { mode }, NO_GATE),
  listActions: async (opts: { all?: boolean; limit?: number; status?: string } = {}) => {
    const params: Record<string, any> = { limit: opts.limit ?? 50 };
    if (opts.all) params.all = 1;
    if (opts.status) params.status = opts.status;
    const res = await api.get(`${BASE}/actions`, { params, ...NO_GATE });
    return pickArray<AssistantAction>(res.data, "data", "actions");
  },
  getUsage: async (): Promise<{ used: number; limit: number | null }> => {
    const res = await api.get(`${BASE}/usage`, NO_GATE);
    const d = res.data?.data || res.data || {};
    return { used: Number(d.used) || 0, limit: d.limit ?? null };
  },
};

// ── Streaming calls (fetch + ReadableStream, Server-Sent Events over POST) ──

export interface PagePayload {
  path: string;
  title?: string;
  entity?: AssistantEntity;
}

/**
 * Parse a text/event-stream body incrementally. Calls onEvent for each
 * JSON `data:` payload. Comment lines (starting with ":") are heartbeats.
 */
async function readEventStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (ev: StreamEvent) => void
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const flushBlock = (block: string) => {
    const dataLines: string[] = [];
    for (const rawLine of block.split(/\r?\n/)) {
      if (!rawLine || rawLine.startsWith(":")) continue;
      if (rawLine.startsWith("data:")) {
        dataLines.push(rawLine.slice(5).replace(/^ /, ""));
      }
      // "event:", "id:", "retry:" are not used by this API
    }
    if (dataLines.length === 0) return;
    const payload = dataLines.join("\n");
    try {
      onEvent(JSON.parse(payload) as StreamEvent);
    } catch {
      // Ignore malformed chunks rather than killing the turn
    }
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let match: RegExpExecArray | null;
    const sep = /\r?\n\r?\n/;
    while ((match = sep.exec(buffer))) {
      const block = buffer.slice(0, match.index);
      buffer = buffer.slice(match.index + match[0].length);
      flushBlock(block);
    }
  }
  buffer += decoder.decode();
  if (buffer.trim()) flushBlock(buffer);
}

async function streamPost(
  path: string,
  body: any,
  onEvent: (ev: StreamEvent) => void,
  signal: AbortSignal
): Promise<void> {
  const token = localStorage.getItem("accessToken") || "";
  let res: Response;
  try {
    res = await fetch(`${GetBaseURL()}${BASE}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        "x-auth-token": token,
      },
      body: JSON.stringify(body ?? {}),
      signal,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") throw err;
    throw new AssistantHttpError(
      "Could not reach Kavoa. Check your connection and try again.",
      0
    );
  }

  if (!res.ok) {
    let data: any = null;
    try {
      data = await res.json();
    } catch {}
    if (res.status === 401) {
      localStorage.removeItem("accessToken");
      throw new AssistantHttpError("Your session expired. Please sign in again.", 401);
    }
    if (res.status === 403 && data?.error === "upgrade_required") {
      throw new AssistantHttpError(
        data?.message || "The assistant is not included in your plan.",
        403,
        "upgrade_required",
        data
      );
    }
    if (res.status === 429) {
      throw new AssistantHttpError(
        "You are sending messages quickly. Wait a moment and try again.",
        429
      );
    }
    throw new AssistantHttpError(
      data?.message || (typeof data?.error === "string" ? data.error : "") ||
        "Something went wrong. Please try again.",
      res.status,
      data?.code
    );
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("text/event-stream") || !res.body) {
    // Fallback: a plain JSON reply
    try {
      const data = await res.json();
      if (data?.type) onEvent(data as StreamEvent);
      else if (Array.isArray(data?.events)) data.events.forEach(onEvent);
    } catch {}
    return;
  }

  await readEventStream(res.body, onEvent);
}

export const assistantStream = {
  sendMessage: (
    threadId: string,
    payload: { content: string; page: PagePayload; mode: AssistantMode },
    onEvent: (ev: StreamEvent) => void,
    signal: AbortSignal
  ) => streamPost(`/threads/${threadId}/messages`, payload, onEvent, signal),
  approve: (
    actionId: string,
    edits: Record<string, string> | undefined,
    onEvent: (ev: StreamEvent) => void,
    signal: AbortSignal
  ) =>
    streamPost(
      `/actions/${actionId}/approve`,
      edits && Object.keys(edits).length ? { edits } : {},
      onEvent,
      signal
    ),
  skip: (actionId: string, onEvent: (ev: StreamEvent) => void, signal: AbortSignal) =>
    streamPost(`/actions/${actionId}/skip`, {}, onEvent, signal),
};
